import React, { useState, useEffect } from 'react';
import { 
  Coins, 
  ShoppingCart, 
  Gift, 
  History, 
  Zap, 
  Heart, 
  MessageCircle, 
  Star, 
  Eye, 
  Filter,
  RotateCcw,
  Brain,
  TrendingUp,
  Crown,
  Calendar,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreditDashboard = () => {
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [creditServices, setCreditServices] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('balance');
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userSubscription, setUserSubscription] = useState(null);

  useEffect(() => {
    loadCreditData();
  }, []);

  const loadCreditData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load subscription status first to filter appropriate offers
      const subscriptionRes = await axios.get(`${API}/subscription/status`, { headers });
      setUserSubscription(subscriptionRes.data);

      // Load all credit data in parallel
      const [balanceRes, packagesRes, servicesRes, historyRes] = await Promise.all([
        axios.get(`${API}/credits/balance`, { headers }),
        axios.get(`${API}/credits/packages`, { headers }), // Now with headers for subscription-aware filtering
        axios.get(`${API}/credits/services`, { headers }),
        axios.get(`${API}/credits/history`, { headers })
      ]);

      setCreditBalance(balanceRes.data);
      
      // Filter credit packages and services based on subscription tier
      const userTier = subscriptionRes.data.subscription?.tier || 'free';
      const filteredPackages = filterPackagesForTier(packagesRes.data.packages, userTier);
      const filteredServices = filterServicesForTier(servicesRes.data.services, userTier);
      
      setCreditPackages(filteredPackages);
      setCreditServices(filteredServices);
      // Ensure creditHistory is always an array
      setCreditHistory(historyRes.data.transactions || []);
    } catch (error) {
      console.error('Error loading credit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPackagesForTier = (packages, tier) => {
    if (tier === 'vip' || tier === 'premium') {
      // Apply VIP discounts and mark as VIP pricing
      return packages.map(pkg => ({
        ...pkg,
        original_price: pkg.price,
        price: Math.round(pkg.price * 0.8 * 100) / 100, // 20% VIP discount
        vip_discount: true,
        discount_percentage: 20
      }));
    }
    return packages;
  };

  const filterServicesForTier = (services, tier) => {
    if (!services) return [];
    
    // Features that VIP users already have access to (should be filtered out)
    const vipIncludedFeatures = [
      'ai_icebreaker_generator',
      'priority_matching', 
      'premium_filters',
      'unlimited_likes',
      'read_receipts'
    ];
    
    if (tier === 'vip') {
      // Remove services that VIP users already have
      return services.filter(service => 
        !vipIncludedFeatures.includes(service.service_id)
      ).map(service => ({
        ...service,
        price: Math.round(service.price * 0.7 * 100) / 100, // 30% VIP discount on remaining services
        original_price: service.price,
        vip_discount: true
      }));
    } else if (tier === 'premium') {
      // Premium users get smaller discount but keep most services
      const premiumIncludedFeatures = ['unlimited_likes', 'read_receipts'];
      return services.filter(service => 
        !premiumIncludedFeatures.includes(service.service_id)
      ).map(service => ({
        ...service,
        price: Math.round(service.price * 0.85 * 100) / 100, // 15% premium discount
        original_price: service.price,
        premium_discount: true
      }));
    }
    
    return services;
  };

  const purchasePackage = async (packageId) => {
    try {
      setPurchaseLoading(packageId);
      const token = localStorage.getItem('token');
      
      // Get current origin for redirect URLs
      const currentOrigin = window.location.origin;
      
      const response = await axios.post(`${API}/credits/purchase`, {
        package_id: packageId,
        success_url: `${currentOrigin}/credits/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${currentOrigin}/credits/cancel`
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        alert('Failed to get checkout URL. Please try again.');
      }
    } catch (error) {
      console.error('Error creating purchase session:', error);
      alert(error.response?.data?.detail || 'Failed to create purchase session. Please try again.');
    } finally {
      setPurchaseLoading(null);
    }
  };

  const purchaseService = async (serviceId) => {
    try {
      setPurchaseLoading(serviceId);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/credits/services/${serviceId}/purchase`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        alert(`Successfully purchased ${response.data.data.service_name}!`);
        loadCreditData(); // Refresh data
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error purchasing service:', error);
      alert('Failed to purchase service. Please try again.');
    } finally {
      setPurchaseLoading(null);
    }
  };

  const claimDailyBonus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/credits/daily-bonus`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('Daily bonus claimed! +$0.50 credits');
        loadCreditData();
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error claiming daily bonus:', error);
    }
  };

  const getServiceIcon = (iconName) => {
    const icons = {
      zap: Zap,
      heart: Heart,
      'message-circle': MessageCircle,
      star: Star,
      brain: Brain,
      eye: Eye,
      'rotate-ccw': RotateCcw,
      filter: Filter
    };
    const IconComponent = icons[iconName] || Star;
    return <IconComponent className="w-6 h-6" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      profile: 'bg-purple-100 text-purple-800',
      matching: 'bg-rose-100 text-rose-800',
      conversation: 'bg-blue-100 text-blue-800',
      insights: 'bg-green-100 text-green-800',
      visibility: 'bg-orange-100 text-orange-800',
      features: 'bg-indigo-100 text-indigo-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const filteredServices = selectedCategory === 'all' ? 
    creditServices : 
    creditServices.filter(service => service.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600 flex items-center justify-center overflow-x-hidden">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-400 via-pink-500 to-purple-600">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Credit Center</h1>
          <p className="text-white/80">Purchase credits and unlock premium dating features</p>
          
          {/* Subscription Status Alert */}
          {userSubscription?.subscription?.tier === 'vip' && (
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-center space-x-3">
                <Crown className="w-5 h-5 text-yellow-400" />
                <div>
                  <h3 className="text-yellow-300 font-semibold">VIP Member Benefits Active</h3>
                  <p className="text-yellow-200 text-sm">
                    Enjoy exclusive 20% off all credit packages and 30% off premium features. 
                    Some features are already included in your VIP subscription.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {userSubscription?.subscription?.tier === 'premium' && (
            <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-center space-x-3">
                <Star className="w-5 h-5 text-purple-400" />
                <div>
                  <h3 className="text-purple-300 font-semibold">Premium Member Benefits</h3>
                  <p className="text-purple-200 text-sm">
                    Enjoy 15% off premium features as a Premium subscriber.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Credit Balance Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-yellow-500 rounded-full p-3">
                <Coins className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  ${creditBalance?.total_balance?.toFixed(2) || '0.00'}
                </h2>
                <p className="text-white/80">Available Credits</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {creditBalance?.expiring_soon > 0 && (
                <div className="bg-orange-500/20 text-orange-200 px-3 py-2 rounded-lg">
                  <p className="text-sm">
                    ${creditBalance.expiring_soon.toFixed(2)} expiring soon
                  </p>
                </div>
              )}
              
              <button
                onClick={claimDailyBonus}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Gift className="w-4 h-4" />
                <span>Daily Bonus</span>
              </button>
            </div>
          </div>

          {/* Credit Breakdown */}
          {creditBalance?.breakdown && Object.keys(creditBalance.breakdown).length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(creditBalance.breakdown).map(([type, data]) => (
                <div key={type} className="bg-white/10 rounded-lg p-3">
                  <p className="text-white/80 text-sm capitalize">{type.replace('_', ' ')}</p>
                  <p className="text-white font-semibold">${data.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation Tabs - Responsive with scroll */}
        <div className="mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2">
            <div className="flex space-x-2 overflow-x-auto pb-1 scrollbar-thin">
              {[
                { id: 'balance', label: 'Balance', icon: Coins },
                { id: 'buy', label: 'Buy Credits', icon: ShoppingCart },
                { id: 'services', label: 'Premium Features', icon: Crown },
                { id: 'history', label: 'History', icon: History }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-xl transition-colors whitespace-nowrap flex-shrink-0 text-sm sm:text-base ${
                    activeTab === tab.id
                      ? 'bg-white text-rose-500'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <tab.icon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="flex-shrink-0">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Balance Tab */}
          {activeTab === 'balance' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Transactions */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {creditBalance?.recent_transactions?.slice(0, 5).map((transaction) => (
                    <div key={transaction.transaction_id} className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-medium">
                          {transaction.service_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-white/60 text-sm">
                          {new Date(transaction.transaction_date).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-red-300 font-semibold">
                        -${transaction.amount_used.toFixed(2)}
                      </span>
                    </div>
                  )) || (
                    <p className="text-white/80">No recent transactions</p>
                  )}
                </div>
              </div>

              {/* Credit Stats */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Credit Statistics</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-white/80">Total Balance:</span>
                    <span className="text-white font-semibold">
                      ${creditBalance?.total_balance?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Credits Expiring Soon:</span>
                    <span className="text-orange-300 font-semibold">
                      ${creditBalance?.expiring_soon?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/80">Total Transactions:</span>
                    <span className="text-white font-semibold">
                      {creditBalance?.recent_transactions?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buy Credits Tab */}
          {activeTab === 'buy' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {creditPackages.map((pkg) => (
                <div
                  key={pkg.package_id}
                  className={`bg-white/10 backdrop-blur-md rounded-2xl p-6 relative ${
                    pkg.popular ? 'ring-2 ring-yellow-400' : ''
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-2">{pkg.name}</h3>
                    <p className="text-white/80 text-sm mb-4">{pkg.description}</p>
                    
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-white">
                        ${pkg.price}
                        {pkg.vip_discount && (
                          <span className="text-sm line-through text-gray-400 ml-2">
                            ${pkg.original_price}
                          </span>
                        )}
                      </div>
                      {pkg.vip_discount && (
                        <div className="bg-yellow-500/20 text-yellow-300 px-2 py-1 rounded text-xs mb-2">
                          🎉 VIP Discount: {pkg.discount_percentage}% OFF
                        </div>
                      )}
                      <div className="text-green-300 text-sm">
                        ${pkg.total_credits} total credits
                      </div>
                      {pkg.bonus_credits > 0 && (
                        <div className="text-yellow-300 text-sm">
                          +${pkg.bonus_credits} bonus!
                        </div>
                      )}
                    </div>
                    
                    <div className="mb-4 space-y-1">
                      {pkg.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-white/80 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-400 mr-2" />
                          {feature}
                        </div>
                      ))}
                    </div>
                    
                    {pkg.savings_percentage > 0 && (
                      <div className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm mb-4">
                        {pkg.savings_percentage}% Extra Value
                      </div>
                    )}
                    
                    <button
                      onClick={() => purchasePackage(pkg.package_id)}
                      disabled={purchaseLoading === pkg.package_id}
                      className={`w-full py-3 rounded-xl font-semibold transition-colors ${
                        pkg.popular
                          ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                          : 'bg-rose-600 hover:bg-rose-700 text-white'
                      } ${purchaseLoading === pkg.package_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {purchaseLoading === pkg.package_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                      ) : (
                        'Purchase Credits'
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div>
              {/* Category Filter */}
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {['all', 'profile', 'matching', 'conversation', 'insights', 'visibility', 'features'].map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category
                          ? 'bg-white text-rose-500'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {category === 'all' ? 'All Features' : category.charAt(0).toUpperCase() + category.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* No Services Message for VIP */}
              {filteredServices.length === 0 && userSubscription?.subscription?.tier === 'vip' && (
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
                  <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">All Premium Features Included!</h3>
                  <p className="text-white/80">
                    As a VIP member, you already have access to all our premium features. 
                    Your subscription includes unlimited access to our entire feature suite.
                  </p>
                </div>
              )}

              {/* Services Grid */}
              {filteredServices.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredServices.map((service) => (
                  <div key={service.service_id} className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="bg-rose-500 rounded-lg p-2">
                          {getServiceIcon(service.icon)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{service.name}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(service.category)}`}>
                            {service.category}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-white">
                          ${service.price || service.cost}
                          {(service.vip_discount || service.premium_discount) && (
                            <span className="text-sm line-through text-gray-400 ml-2">
                              ${service.original_price}
                            </span>
                          )}
                        </div>
                        {service.vip_discount && (
                          <div className="text-xs text-yellow-300">VIP: 30% OFF</div>
                        )}
                        {service.premium_discount && (
                          <div className="text-xs text-purple-300">Premium: 15% OFF</div>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-white/80 text-sm mb-4">{service.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      {service.features.map((feature, index) => (
                        <div key={index} className="flex items-center text-white/80 text-sm">
                          <Star className="w-3 h-3 text-yellow-400 mr-2" />
                          {feature}
                        </div>
                      ))}
                    </div>
                    
                    {service.duration_hours && (
                      <div className="text-white/60 text-sm mb-4">
                        Duration: {service.duration_hours < 24 ? 
                          `${service.duration_hours} hours` : 
                          `${Math.floor(service.duration_hours / 24)} days`
                        }
                      </div>
                    )}
                    
                    <button
                      onClick={() => purchaseService(service.service_id)}
                      disabled={purchaseLoading === service.service_id || creditBalance?.total_balance < service.cost}
                      className={`w-full py-2 rounded-lg font-semibold transition-colors ${
                        creditBalance?.total_balance >= service.cost
                          ? 'bg-rose-600 hover:bg-rose-700 text-white'
                          : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                      } ${purchaseLoading === service.service_id ? 'opacity-50' : ''}`}
                    >
                      {purchaseLoading === service.service_id ? (
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                      ) : creditBalance?.total_balance >= service.cost ? (
                        'Purchase'
                      ) : (
                        'Insufficient Credits'
                      )}
                    </button>
                  </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Credit History</h3>
              
              <div className="space-y-4">
                {creditHistory && creditHistory.length > 0 ? (
                  creditHistory.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white/10 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          item.type === 'credit_added' ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                          {item.type === 'credit_added' ? (
                            <TrendingUp className="w-4 h-4 text-white" />
                          ) : (
                            <ShoppingCart className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{item.description}</p>
                          <p className="text-white/60 text-sm">
                            {new Date(item.date).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className={`font-semibold ${
                        item.amount > 0 ? 'text-green-300' : 'text-red-300'
                      }`}>
                        {item.amount > 0 ? '+' : ''}${Math.abs(item.amount).toFixed(2)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Clock className="h-16 w-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/80 text-lg mb-2">No Credit History</p>
                    <p className="text-white/60 text-sm">Your credit transactions will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditDashboard;